import { SubmitPlayerGameVotes, VoteType, GetGameViewById } from "@grassrootly/api";
import useBaseRequest from "../../api/BaseRequest";
import { useUser } from "../../api/auth/AuthSelector";
import { useGameView } from "../../api/game/GameViewSelector";
import { useAppDispatch } from "@grassrootly/rn-core";
import { gameViewActions } from "../../api/game/GameViewReducer";
import { adaptGameViewToGame } from "../game/gameViewAdapter";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../navigation/Navigation.types";
import { useCallback, useEffect, useMemo } from "react";
import { useAlert } from "@grassrootly/rn-core";
import { IGameAttendances } from "@grassrootly/api";

export function useGameVoteOrganization(
  gameId: number,
  navigation: NativeStackNavigationProp<RootStackParamList, "vote">
) {
  const user = useUser();
  const dispatch = useAppDispatch();
  const gameView = useGameView(gameId);
  const game = gameView ? adaptGameViewToGame(gameView) : null;
  const { danger } = useAlert();

  // Function to refresh game data after voting
  const { execute: refreshGameView, loading: fetching } = useBaseRequest(GetGameViewById, {
    onCompleted: (data) => {
      if (!data) return;
      dispatch(gameViewActions.setGameView(data));
    },
  });

  const fetchGame = () => {
    refreshGameView({ id: gameId });
  };

  // Fetch game data on mount if not already loaded
  useEffect(() => {
    if (!gameView && !fetching) {
      fetchGame();
    }
  }, [gameId, gameView, fetching]);

  const { execute, loading: submitting } = useBaseRequest(SubmitPlayerGameVotes, {
    onCompleted: (data) => {
      fetchGame(); // Refresh game data to get updated vote information
      navigation.goBack();
    },
    onError: (error) => {
      console.error('Vote submission error:', error);
      // Always refresh game data even on error to ensure UI is in sync
      fetchGame();
      // Navigate back so user doesn't get stuck on vote screen
      navigation.goBack();
    },
  });

  const submitVotes = (votes: Array<{ type: VoteType; attendanceId: number | null }>) => {
    // Prevent double submission if already submitting
    if (submitting) {
      return;
    }

    if (!hasAttended()) {
      danger({ message: "Only players that have played the game can vote.", title: "Not allowed" });
      return;
    }

    // Filter out null votes ("Not sure" selections)
    const validVotes = votes.filter((v) => v.attendanceId !== null);

    if (validVotes.length === 0) {
      danger({ message: "Please select at least one vote.", title: "No votes selected" });
      return;
    }

    execute({
      votes: validVotes.map((v) => ({ ...v, id: user?.id!, attendanceId: v.attendanceId! })),
    });
  };

  const hasAttended = useCallback(() => {
    return !!game?.attendees?.find((a) => a.player_id === user?.id && a.attended);
  }, [game, user]);

  // Get current player's attendance to determine their team
  const currentPlayerAttendance = useMemo(() => {
    return game?.attendees?.find((a) => a.player_id === user?.id);
  }, [game, user]);

  // Filter opponents (players from the other team)
  const opponents = useMemo(() => {
    if (!game || !currentPlayerAttendance) return [];
    
    // Use playing_for_group_id if available, otherwise fall back to team field
    const currentTeamId = currentPlayerAttendance.playing_for_group_id;
    const currentTeam = currentPlayerAttendance.team;
    
    return game.attendees.filter((a) => {
      if (!a.attended || a.player_id === user?.id) return false;
      
      // If playing_for_group_id is available, use it
      if (currentTeamId != null && a.playing_for_group_id != null) {
        return a.playing_for_group_id !== currentTeamId;
      }
      
      // Fall back to team field (HOST/VISITOR)
      if (currentTeam && a.team) {
        return a.team !== currentTeam;
      }
      
      return false;
    });
  }, [game, currentPlayerAttendance, user]);

  // Filter teammates (players from the same team, excluding self)
  const teammates = useMemo(() => {
    if (!game || !currentPlayerAttendance) return [];
    
    // Use playing_for_group_id if available, otherwise fall back to team field
    const currentTeamId = currentPlayerAttendance.playing_for_group_id;
    const currentTeam = currentPlayerAttendance.team;
    
    return game.attendees.filter((a) => {
      if (!a.attended || a.player_id === user?.id) return false;
      
      // If playing_for_group_id is available, use it
      if (currentTeamId != null && a.playing_for_group_id != null) {
        return a.playing_for_group_id === currentTeamId;
      }
      
      // Fall back to team field (HOST/VISITOR)
      if (currentTeam && a.team) {
        return a.team === currentTeam;
      }
      
      return false;
    });
  }, [game, currentPlayerAttendance, user]);

  const goBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    navigation.replace("main");
  };

  return {
    game,
    opponents,
    teammates,
    saving: fetching || submitting,
    submitVotes,
    goBack,
  };
}
